import { useTranslation } from 'react-i18next';

import { Box, Chip, LinearProgress, Stack, Tooltip, Typography } from '@mui/material';

import { formatNumber, formatPrice } from '../../lib/format';
import type { BudgetResponse } from '../../model/types';

export interface BudgetBurnBarProps {
  budget: BudgetResponse;
}

/**
 * Budget burn indicator (plan-011 §8.6). Color: `primary` below the alert threshold, `warning`
 * at/above it, `error` at 100%. Never color-only — the percentage text (and the exhausted chip)
 * always carry the state too. Until the burn consumer ships (Phase 3), `consumedValue` reads 0 —
 * degrades to a 0% bar with an explanatory caption instead of a misleading/dead chart.
 */
export function BudgetBurnBar({ budget }: BudgetBurnBarProps) {
  const { t } = useTranslation('promotions');
  const threshold = budget.alertThresholdPct ?? 80;
  const percent = budget.capValue > 0 ? Math.min(100, Math.round((budget.consumedValue / budget.capValue) * 100)) : 0;
  const exhausted = Boolean(budget.exhaustedAt) || percent >= 100;
  const color = exhausted ? 'error' : percent >= threshold ? 'warning' : 'primary';

  const valueLabel =
    budget.capKind === 'MONEY'
      ? t('budgets.burn.moneyValue', { consumed: formatPrice(budget.consumedValue), cap: formatPrice(budget.capValue) })
      : t('budgets.burn.redemptionsValue', {
          consumed: formatNumber(budget.consumedValue),
          cap: formatNumber(budget.capValue),
        });

  return (
    <Stack spacing={0.5}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
          {valueLabel}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
            {percent}%
          </Typography>
          {exhausted ? <Chip size="small" color="error" label={t('budgets.exhausted')} /> : null}
        </Stack>
      </Stack>
      <Box sx={{ position: 'relative' }}>
        <LinearProgress
          variant="determinate"
          value={percent}
          color={color}
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={t('budgets.burn.ariaLabel', { percent })}
          sx={{ height: 10, borderRadius: 999 }}
        />
        <Tooltip title={t('budgets.burn.thresholdTooltip', { threshold })}>
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              top: -2,
              bottom: -2,
              left: `${threshold}%`,
              width: 2,
              bgcolor: 'text.primary',
              opacity: 0.5,
            }}
          />
        </Tooltip>
      </Box>
      {budget.consumedValue === 0 ? (
        <Typography variant="caption" color="text.secondary">
          {t('budgets.burn.noSpendYet')}
        </Typography>
      ) : null}
      {budget.period === 'DAILY' ? (
        <Typography variant="caption" color="text.secondary">
          {t('budgets.burn.dailyResetHint')}
        </Typography>
      ) : null}
    </Stack>
  );
}
